import {
  Box,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { useCloudFormationValidateAccount } from '@/components/accounts/validateAccount';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';

import { AwsAccountConnectionFields } from './awsAccountConnectionFields';

import { StepErrorDialog } from '../../../../../components/dialogs/stepErrorDialog';
import { BulletItem } from '../bulletItem';
import { extractErrorMessage } from '../errorHandling';
import { NumericBulletSection } from '../numericBulletSection';

const accountIdConfig = getCloudFieldConfig(Provider.Aws, 'accountId');

export interface AwsConnectRestoreAccountState {
  accountId: string;
  roleName: string;
  deploymentMethod: 'cloudformation' | 'terraform';
  eonAccountId?: string;
}

export const ConnectAwsRestoreAccount = (
  props: StepProps<AwsConnectRestoreAccountState>
) => {
  const [openCloudFormationClicked, setOpenCloudFormationClicked] =
    useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [customName, setCustomName] = useState('');

  const { openCloudFormationValidateAccount } =
    useCloudFormationValidateAccount();
  const dal = useDAL();
  const { user } = useUser();

  const { accountId, roleName, deploymentMethod } = props.wizardState;

  const isMissingFields = !accountId || !roleName;
  const isAccountIdValid = accountIdConfig.validation.validateInput(accountId);

  const canGoNext = () => {
    if (isDeploying || isMissingFields || !isAccountIdValid) return false;
    if (deploymentMethod === 'cloudformation' && !openCloudFormationClicked)
      return false;
    return true;
  };

  const openCloudFormation = () => {
    setOpenCloudFormationClicked(true);
    openCloudFormationValidateAccount(roleName, CloudAccountType.Restore);
  };

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      const { restoreAccount } = await dal.cloudAccounts.restore.connect.aws({
        role: `arn:aws:iam::${accountId}:role/${roleName}`,
        name: customName || '',
      });
      props.setWizardState({
        ...props.wizardState,
        eonAccountId: restoreAccount.id ?? '',
      });
      await props.finish();
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const nextButtonTooltip = isDeploying
    ? 'Please wait for the deployment to complete'
    : isMissingFields
      ? 'Please fill the missing fields'
      : !isAccountIdValid
        ? 'Please enter a valid AWS account ID'
        : deploymentMethod === 'cloudformation' && !openCloudFormationClicked
          ? 'Please click the button to launch CloudFormation'
          : '';

  const terraformCode = `module "eon_restore_account_infra" {
  source         = "https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/eon-aws-restore-account-tf.zip"
  eon_account_id = "${user?.eonAccount.id ?? ''}"
  role_name      = "${roleName}"
}`;

  return (
    <>
      <StepContainer
        stepperIndex={3}
        toolbarComponent={
          <Stack paddingX='40px' direction='row' justifyContent='space-between'>
            <Typography width='540px' variant='body1'>
              Before you start, make sure you can manage roles in your AWS
              environment.
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={deploymentMethod}
              onChange={(_, value) => {
                if (value === null) return;
                setOpenCloudFormationClicked(false);
                props.setWizardState({
                  ...props.wizardState,
                  deploymentMethod: value as 'cloudformation' | 'terraform',
                });
              }}
            >
              <ToggleButton value='cloudformation'>CloudFormation</ToggleButton>
              <ToggleButton value='terraform'>Terraform</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={nextButtonTooltip}
        nextButtonText='Connect'
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          {deploymentMethod === 'cloudformation' ? (
            <Stack gap='min(60px, 4vw)' flexGrow={1} divider={<Divider />}>
              <Box paddingX='min(100px,6vw)' height='auto'>
                <NumericBulletSection
                  bulletNumber={1}
                  title='Set connection details'
                >
                  <BulletItem
                    leftContent={
                      <AwsAccountConnectionFields
                        accountId={accountId}
                        setAccountId={(accountId) =>
                          props.setWizardState({
                            ...props.wizardState,
                            accountId,
                          })
                        }
                        roleName={roleName}
                        setRoleName={(roleName) =>
                          props.setWizardState({
                            ...props.wizardState,
                            roleName,
                          })
                        }
                        customName={customName}
                        setCustomName={setCustomName}
                      />
                    }
                  />
                </NumericBulletSection>
              </Box>
              <Box paddingX='min(100px,6vw)' width='100%' height='auto'>
                <NumericBulletSection
                  bulletNumber={2}
                  title='Review and deploy'
                >
                  <BulletItem
                    title="Launch CloudFormation to review and deploy Eon's permissions in your AWS account. Afterward, come back to this tab to finish the connection."
                    leftContent={
                      <ButtonWithIcon
                        sx={{ width: '250px' }}
                        disabled={isMissingFields}
                        variant='contained'
                        color='primary'
                        text='Launch CloudFormation in AWS'
                        icon='material-symbols-open-in-new'
                        iconPosition='right'
                        onClick={openCloudFormation}
                      />
                    }
                  />
                </NumericBulletSection>
              </Box>
            </Stack>
          ) : (
            <Box padding='16px min(100px,6vw) 36px' width='100%' height='auto'>
              <BulletItem
                title='Fill in your account details, then apply the generated Terraform module in your AWS environment.'
                leftContent={
                  <AwsAccountConnectionFields
                    accountId={accountId}
                    setAccountId={(accountId) =>
                      props.setWizardState({
                        ...props.wizardState,
                        accountId,
                      })
                    }
                    roleName={roleName}
                    setRoleName={(roleName) =>
                      props.setWizardState({
                        ...props.wizardState,
                        roleName,
                      })
                    }
                    customName={customName}
                    setCustomName={setCustomName}
                  />
                }
                rightContent={
                  <CopyableCode
                    title='main.tf'
                    code={terraformCode}
                    blur={isMissingFields}
                  />
                }
              />
            </Box>
          )}
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
